<script lang="ts">
	import TableOptionsFilterSelect from 'components/TableOptionsFilterSelect/TableOptionsFilterSelect.svelte';
	import MarketsTable from './MarketsTable/MarketsTable.svelte';
	import { _ } from 'utils/i18/i18n';
	import activeLocale from 'stores/activeLocale';
	import FilterButton from 'components/FilterButton/FilterButton.svelte';
	import { page } from '$app/stores';
	import { getExchangeMarketsData } from 'services/exchanges/exchange-markets';
	import type { IFormattedMarket, IMarketsData, MarketsSortType } from 'customTypes/exchange';
	import { goto } from '$app/navigation';
	import FilterWithSearch from '../FilterWithSearch/FilterWithSearch.svelte';

	export let dark = false;
	export let name: string;

	$: normalizedLocale = $activeLocale.locale === 'en' ? '' : `/${$activeLocale.locale}/`;
	$: id = $page?.params?.exchange;
	$: filterType = ($page?.url?.searchParams?.get('type') as 'spot' | 'derivative') || 'spot';
	let filterList = [{ value: 'all', name: 'all' }];
	$: currentPage = Number($page.url.searchParams.get('page')) || 1;

	let filterCurrency = { value: 'all', name: 'all' };
	let perPage = 100;
	let totalPages: number;
	let tableData: IFormattedMarket[] = [];
	let sortBy: MarketsSortType = 'volume';
	let sortDirection: 'asc' | 'desc' = 'desc';
	let marketTypes: { value: string; name: string }[] = [];
	let isLoading = false;

	const typeBtnTitle = {
		spot: $_('exchanges.spot'),
		derivative: $_('exchanges.derivative'),
	};

	async function getData() {
		isLoading = true;
		const data: IMarketsData = await getExchangeMarketsData(
			id,
			currentPage,
			perPage,
			filterCurrency.value,
			filterCurrency.name,
			sortBy,
			sortDirection,
			filterType,
		);
		tableData = data?.markets;
		totalPages = data?.totalPages;
		marketTypes = data?.marketsTypes.map((type) => ({
			value: type,
			name: type,
		}));

		if (data && data?.filters) {
			filterList = [
				{ value: 'all', name: 'all' },
				...data?.filters.map((filter) => ({ value: filter?.coinId, name: filter?.base })),
			];
		} else {
			filterList = [{ value: 'all', name: 'all' }];
		}
		isLoading = false;
	}

	function handleTypeSelect(type: 'spot' | 'derivative') {
		goto(`/markets/${id}${normalizedLocale}?type=${type}&page=1`);
	}
	function handleCurrencySelect(selectedValue) {
		filterCurrency = selectedValue;
		currentPage = 1;
	}

	$: currentPage, filterCurrency, sortBy, sortDirection, filterType, getData();
	$: filterType, (filterCurrency = { value: 'all', name: 'all' });
</script>

<div class="wrapper" class:dark>
	<div class="container">
		<h1 class="title">{`${name} `}<br />{$_('exchange.markets')}</h1>
		<div class="filters">
			<div class="type-filter">
				{#each marketTypes as type}
					<FilterButton
						as="link"
						linkTo={`/markets/${id}${normalizedLocale}?type=${type.value}&page=1`}
						{dark}
						isActive={filterType === type.value}
						title={typeBtnTitle[type.value]}
						id={type.value}
					/>
				{/each}
			</div>
			<div class="type-filter mobile">
				{#if marketTypes.length > 0}
					<TableOptionsFilterSelect
						variants={marketTypes}
						handleClick={handleTypeSelect}
						{dark}
						minWidth={138}
						alignStart
					/>
				{/if}
			</div>
			<FilterWithSearch
				filters={filterList}
				handleClick={handleCurrencySelect}
				{dark}
				minWidth={138}
				selectObject
				selectedScale={filterCurrency}
			/>
		</div>
	</div>

	<MarketsTable
		{dark}
		{tableData}
		{filterType}
		bind:currentPage
		{totalPages}
		{perPage}
		bind:sortBy
		bind:sortDirection
		{isLoading}
		{id}
	/>
</div>

<style lang="scss">
	@import './exchangeMarkets.scss';
</style>
